import { createContextualCan } from '@casl/react'
import { createContext, ReactNode, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { useGetResellers } from '@/api/useGetResellers'
import { useGetServices } from '@/api/useGetServices'
import { ScreenLoader } from '@/components/feedbacks'
import { useRefreshToken } from '@/features/auth'
import { useGetUserProfile } from '@/features/profile'
import { ABILITIES, AppAbility } from '@/lib/casl'
import { useAppStore } from '@/stores/appStore'
import { useServicesStore } from '@/stores/servicesStore'

const TOKEN_REFRESH_CHECK_INTERVAL = 2 * 60 * 1000 // Check every 2 minutes
const TOKEN_REFRESH_BUFFER = 10 * 60 * 1000 // Refresh when 10 minutes remain

const AbilityContext = createContext<AppAbility>(ABILITIES.DEFAULT)
const Can = createContextualCan(AbilityContext.Consumer)

/**
 * This provider will fetch the user profile and parameters from the API if the user is authenticated.
 * It also setup the app global store on/from local storage.
 * @param children content to render
 * @returns content to render
 */
const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const accessToken = useAppStore((state) => state.accessToken)
  const hasAccessToken = Boolean(accessToken)
  const {
    data: authenticatedUser,
    isLoading: authenticatedUserLoading,
    error: authenticatedUserError,
    isSuccess,
  } = useGetUserProfile({ config: { enabled: hasAccessToken } })

  const { setAbility, setIsAuthenticated, clearTokens, clearSessionId, setUser, setResellers } = useAppStore(
    (state) => state.actions
  )
  const ability = useAppStore((state) => state.ability)
  const storedUser = useAppStore((state) => state.user)
  const refreshTokenValue = useAppStore((state) => state.refreshToken)
  const tokenExpiresAt = useAppStore((state) => state.tokenExpiresAt)

  // Services availability, fetched once at app launch
  const { data: services } = useGetServices({ config: { enabled: hasAccessToken } })
  const setServices = useServicesStore((state) => state.actions.setServices)
  const { data: resellers, isLoading: resellersLoading } = useGetResellers({ config: { enabled: hasAccessToken } })

  useEffect(() => {
    if (services) {
      setServices(services)
    }
  }, [services, setServices])

  useEffect(() => {
    if (resellers) {
      setResellers(resellers)
    }
  }, [resellers, setResellers])

  const signOutLocally = useCallback(() => {
    clearTokens()
    clearSessionId()
    setIsAuthenticated(false)
    setAbility(ABILITIES.DEFAULT)
    navigate('/')
  }, [clearTokens, clearSessionId, setIsAuthenticated, setAbility, navigate])

  const { mutate: refreshTokenMutate } = useRefreshToken({
    config: {
      onError: signOutLocally,
    },
  })

  const isFetchingApi = authenticatedUserLoading || resellersLoading
  const error = authenticatedUserError

  // Stable callback for token refresh
  const checkTokenExpiration = useCallback(() => {
    if (!refreshTokenValue || !tokenExpiresAt) {
      return
    }

    const timeUntilExpiration = tokenExpiresAt - Date.now()

    // Refresh token if it expires within the buffer time (or has already expired)
    if (timeUntilExpiration <= TOKEN_REFRESH_BUFFER) {
      refreshTokenMutate(refreshTokenValue)
    }
  }, [refreshTokenValue, tokenExpiresAt, refreshTokenMutate])

  // Check token expiration periodically and on mount
  useEffect(() => {
    // Immediate check on mount or when dependencies change
    checkTokenExpiration()

    // Then check every interval
    const interval = setInterval(checkTokenExpiration, TOKEN_REFRESH_CHECK_INTERVAL)

    return () => clearInterval(interval)
  }, [checkTokenExpiration])

  useEffect(() => {
    if (!isFetchingApi && !error && isSuccess) {
      setIsAuthenticated(true)
      setAbility(ABILITIES.DEFAULT)
    }
  }, [setAbility, authenticatedUser, isFetchingApi, isSuccess, setIsAuthenticated, error])

  // Re-syncs the store whenever it drifts from the fetched profile, e.g. after a concurrent token
  // refresh failure cleared it while the session stayed valid.
  useEffect(() => {
    if (hasAccessToken && authenticatedUser && authenticatedUser !== storedUser) {
      setUser(authenticatedUser)
    }
  }, [hasAccessToken, authenticatedUser, storedUser, setUser])

  // An authenticated session without a profile is inconsistent: sign out instead of rendering an empty shell.
  useEffect(() => {
    if (hasAccessToken && error) {
      signOutLocally()
    }
  }, [hasAccessToken, error, signOutLocally])

  if (isFetchingApi) return <ScreenLoader />

  return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>
}

export { AuthProvider, Can }
